import { useEffect, useMemo, useState } from 'react';
import CustomTextField from '../../../components/UI/Inputs/CustomTextField';
import Modal from '../../../components/UI/Modals/Modal';
import {
  IDocument,
  IDocumentInput,
  selectDocumentTypes,
  fetchDocumentTypes,
} from '../../../store/slices/documents';
import classes from './NewDocumentModal.module.css';
import WebcamCapture from '../../../components/WebCam/webcam';

import PrimaryButtonV2 from '../../../components/UI/Buttons/PrimaryButtonV2';
import { DocumentCard } from '../../../components/DocumentList/DocumentCard';
import React from 'react';
import CustomDropdown from '../../../components/UI/Inputs/CustomDropdown';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { IIdAndLabel } from '../../../components/UI/Inputs/ModalDropdown';
import { v4 as uuidv4 } from 'uuid';
import VideoCapture, { VideoDataProps } from '../../../components/WebCam/VideoCapture';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  variant: 'capture' | 'upload';
  selectedFile: File;
  setSelectedFile: (file: File) => void;
  handleSave: (document: IDocumentInput) => void;
};

const NewDocumentModal: React.FC<Props> = ({
  isOpen,
  onClose,
  variant,
  selectedFile,
  setSelectedFile,
  handleSave,
}) => {
  const { translations } = useTranslation();
  const dispatch = useAppDispatch();
  const documentTypes = useAppSelector(selectDocumentTypes);
  useEffect(() => {
    if (selectedFile?.type) {
      if (
        !selectedFile?.type.includes('pdf') &&
        !selectedFile?.type.includes('image') &&
        !selectedFile?.type.includes('video')
      ) {
        onClose();
      }
    }
  }, [selectedFile?.type]);

  useEffect(() => {
    dispatch(fetchDocumentTypes());
  }, [dispatch]);

  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [documentComments, setDocumentComments] = useState<string>('');
  const [modalStep] = useState<'upload' | 'capture' | 'preview' | 'edit'>(variant);
  const [capturedImageData, setCapturedImageData] = useState<string>('');
  const [selectedDocumentType, setSelectedDocumentType] = useState<number>();
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoTranscript, setVideoTranscript] = useState<string>('');
  const [isVideoRecording, setIsVideoRecording] = useState(false);

  const modalTitle = useMemo(() => {
    if (variant === 'upload') return translations('document.information');
    if (modalStep === 'capture' || modalStep === 'preview') return translations('take.image');
    if (modalStep === 'edit') return translations('info.document');
    return '';
  }, [variant, modalStep]);

  const [errorValidation, setErrorValidation] = useState<{ title: boolean; type: boolean }>({
    title: false,
    type: false,
  });

  const handleChange = (value: string, name: string) => {
    if (name === 'title') {
      setDocumentTitle(value);
      setErrorValidation((prev) => ({ ...prev, title: value === '' }));
      return;
    }
    setSelectedDocumentType(parseInt(value));
    setErrorValidation((prev) => ({ ...prev, type: !Boolean(value) }));
  };

  const handleSaveVideo = (data: VideoDataProps) => {
    if (data.blob) {
      const file = new File([data.blob], 'captured.mp4', {
        type: 'video/mp4',
      });
      setSelectedFile(file);
      setVideoUrl(data.videoUrl);
      setVideoTranscript(data.transcript);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      containerPadding="40px 40px 50px 40px"
      header={modalTitle}
      width="85%"
      maxWidth="580px"
    >
      <div className={classes.container}>
        {variant === 'capture' && modalStep === 'capture' ? (
          <>
            <div className={classes.captureTabsContainer}>
              <div
                className={`${classes.captureTab} ${isVideoRecording ? '' : classes.selectedTab}`}
                onClick={() => setIsVideoRecording(false)}
              >
                {translations("picture")}
              </div>
              <div
                className={`${classes.captureTab} ${isVideoRecording ? classes.selectedTab : ''}`}
                onClick={() => setIsVideoRecording(true)}
              >
                {translations("video")}
              </div>
            </div>
            {isVideoRecording ? (
              <VideoCapture onSaveVideo={handleSaveVideo} />
            ) : (
              <WebcamCapture
                imageData={capturedImageData}
                onCaptureImage={(capturedImage: string) => {
                  setCapturedImageData(capturedImage);
                }}
                onSaveImage={async () => {
                  const fetchedCapturedImage = await fetch(capturedImageData);
                  let blob = await fetchedCapturedImage.blob();
                  setSelectedFile(
                    new File([blob], 'captured.jpeg', {
                      type: 'image/jpeg',
                    })
                  );
                }}
              />
            )}
          </>
        ) : (
          <div className={classes.newDocumentContainer}>
            <div className={classes.documentTextFields}>
              <div style={{ width: '100%', marginBottom: 15 }}>
                <DocumentCard
                  document={
                    {
                      title: documentTitle,
                      comment: documentComments,
                      datetime_created: new Date(),
                      fileSize: selectedFile?.size,
                      name: selectedFile?.name,
                    } as IDocument
                  }
                  newDocumentModal
                  hideComment
                  mimeType={
                    (capturedImageData !== '' ? 'captured.jpeg' : selectedFile?.name ?? '')
                      .split('.')
                      .slice(-1)[0]
                  }
                  isUploading
                />
              </div>
              <CustomTextField
                label={translations('add.title').toString()}
                placeholder={translations('label.enter_title').toString()}
                value={documentTitle}
                onChange={(e: any) => handleChange(e.target.value, 'title')}
                errorValidation={errorValidation.title}
                height="82px"
                labelColor="rgba(17, 19, 31, 1)"
              />
              <div style={{ marginBottom: '30px' }}></div>
              <CustomDropdown
                dropdownStyle={{
                  height: '58px',
                  margin: '0px 3px',
                  color: selectedDocumentType ? 'rgba(31, 31, 31, 1)' : '#AFB2C3',
                  fontSize: 18,
                }}
                maxWidth={'100%'}
                label={translations('label_select.group').toString()}
                placeholder={translations('select').toString()}
                options={documentTypes.map((documentType) => {
                  const result: IIdAndLabel = {
                    id: documentType.attachment_type_id,
                    label: translations(
                      `label.attachment_description_${documentType.attachment_type_description
                        .replace(/ /g, '_')
                        .toLowerCase()}`
                    ),
                  };
                  return result;
                })}
                value={selectedDocumentType}
                onChange={(e: any) => handleChange(e.target.value, 'documentType')}
                labelColor="rgba(17, 19, 31, 1)"
                errorValidation={errorValidation.type}
              />
              <div style={{ marginBottom: '30px' }}></div>
              <CustomTextField
                label={translations('add.comments').toString()}
                value={documentComments}
                onChange={(e: any) => setDocumentComments(e.target.value)}
                height="120px"
                labelColor="rgba(17, 19, 31, 1)"
                optional
              />
            </div>
          </div>
        )}
        {(variant === 'upload' || modalStep === 'edit') && (
          <div style={{ display: 'flex', width: '100%' }}>
            <PrimaryButtonV2
              text={translations('upload')}
              containerStyle={{
                marginTop: '30px',
              }}
              buttonStyle={{
                fontSize: 16,
                height: 60,
                width: 245,
              }}
              onClick={() => {
                if (selectedFile && selectedDocumentType !== undefined && documentTitle !== '') {
                  handleSave({
                    document: {
                      id: uuidv4(),
                      title: documentTitle,
                      comment: documentComments,
                      attachment_type_id: selectedDocumentType,
                      attachment_type_description: documentTypes.find(
                        (el) => el.attachment_type_id === selectedDocumentType
                      )?.attachment_type_description,
                      name: selectedFile.name,
                      link: modalStep === 'upload' ? URL.createObjectURL(selectedFile) : videoUrl,
                      attachment_url: '',
                      transcription_text: videoTranscript,
                      datetime_created: new Date(),
                      mime_type: selectedFile.name.split('.').slice(-1)[0],
                    },
                    file: selectedFile,
                  });
                } else {
                  setErrorValidation({
                    title: documentTitle === '',
                    type: selectedDocumentType === undefined,
                  });
                }
              }}
            />
            <PrimaryButtonV2
              text={translations('cancel')}
              containerStyle={{
                marginTop: '30px',
                marginLeft: 'auto',
              }}
              invertedColors
              buttonStyle={{
                fontSize: 16,
                height: 60,
                width: 245,
              }}
              onClick={onClose}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default NewDocumentModal;
